import { z } from 'zod';
import { layoutFamilyOf, type LayoutFamily, type SoftwareFamily, type SoftwareSpec } from './domain.js';
import { mohistProvider } from './providers-mohist.js';
import { purpurProvider, installerProvider } from './providers-extra.js';
export interface ResolvedArtifact {
  url: string;
  fileName: string;
  hash?: { algo: 'md5' | 'sha1' | 'sha256' | 'sha512'; value: string };
  installMode?: 'jar' | 'installer';
  javaMajor?: number;
  loaderVersion?: string;
  build?: string;
}
export type FetchJson = (url: string) => Promise<unknown>;
export type AddonFetchJson = (url: string, init?: { method: 'POST'; body: string }) => Promise<unknown>;
export class SoftwareUnavailableError extends Error {}
export interface SoftwareProvider {
  family: SoftwareFamily;
  layoutFamily: LayoutFamily;
  listVersions(): Promise<string[]>;
  listBuilds(mcVersion: string): Promise<string[]>;
  recommendedBuild?(mcVersion: string): Promise<string | undefined>;
  resolve(spec: SoftwareSpec): Promise<ResolvedArtifact>;
}
export const MOJANG_MANIFEST = 'https://piston-meta.mojang.com/mc/game/version_manifest_v2.json';
const manifestSchema = z.object({ versions: z.array(z.object({ id: z.string(), url: z.string().url() })) });
const versionSchema = z.object({
  downloads: z.object({ server: z.object({ url: z.string().url(), sha1: z.string() }).optional() }),
  javaVersion: z.object({ majorVersion: z.number().int() }).optional(),
});
const paperBase = 'https://fill.papermc.io/v3/projects/paper';
const paperBuildsSchema = z.array(
  z.object({
    id: z.number().int(),
    channel: z.string(),
    downloads: z.record(
      z.string(),
      z.object({ name: z.string(), url: z.string().url(), checksums: z.object({ sha256: z.string() }) }),
    ),
  }),
);
const fabricBase = 'https://meta.fabricmc.net/v2/versions';
const fabricLoadersSchema = z.array(z.object({ loader: z.object({ version: z.string(), stable: z.boolean() }) }));
export function getProvider(family: SoftwareFamily, fetchJson: FetchJson): SoftwareProvider {
  if (family === 'youer' || family === 'asyncyouer' || family === 'mohist') return mohistProvider(family, fetchJson);
  if (family === 'purpur') return purpurProvider(fetchJson);
  if (family === 'neoforge' || family === 'forge') return installerProvider(family, fetchJson);
  const check = (spec: SoftwareSpec) => {
    if (spec.family !== family) throw new Error('Provider family mismatch');
  };
  if (family === 'vanilla') {
    const versions = async () => manifestSchema.parse(await fetchJson(MOJANG_MANIFEST)).versions;
    return {
      family,
      layoutFamily: layoutFamilyOf(family),
      listVersions: async () => (await versions()).map((v) => v.id),
      listBuilds: async (mc) => {
        if (!(await versions()).some((v) => v.id === mc)) throw new Error(`Unknown version: ${mc}`);
        return [];
      },
      resolve: async (spec) => {
        check(spec);
        const entry = (await versions()).find((v) => v.id === spec.mcVersion);
        if (!entry) throw new Error(`Unknown version: ${spec.mcVersion}`);
        const v = versionSchema.parse(await fetchJson(entry.url));
        if (!v.downloads.server)
          throw new SoftwareUnavailableError(`Vanilla ${spec.mcVersion} はサーバー配布物が無いため選べません`);
        return {
          url: v.downloads.server.url,
          fileName: `vanilla-${spec.mcVersion}.jar`,
          hash: { algo: 'sha1', value: v.downloads.server.sha1 },
          javaMajor: v.javaVersion?.majorVersion,
        };
      },
    };
  }
  if (family === 'paper') {
    const builds = async (mc: string) =>
      paperBuildsSchema.parse(await fetchJson(`${paperBase}/versions/${encodeURIComponent(mc)}/builds`));
    return {
      family,
      layoutFamily: 'bukkit',
      listVersions: async () =>
        Object.values(
          z.object({ versions: z.record(z.string(), z.array(z.string())) }).parse(await fetchJson(paperBase)).versions,
        ).flat(),
      listBuilds: async (mc) => (await builds(mc)).toSorted((a, b) => b.id - a.id).map((b) => String(b.id)),
      recommendedBuild: async (mc) => {
        const stable = (await builds(mc)).filter((b) => b.channel === 'STABLE').toSorted((a, b) => b.id - a.id)[0];
        return stable && String(stable.id);
      },
      resolve: async (spec) => {
        check(spec);
        const all = await builds(spec.mcVersion);
        const build = spec.build
          ? all.find((b) => String(b.id) === spec.build)
          : all.filter((b) => b.channel === 'STABLE').toSorted((a, b) => b.id - a.id)[0];
        const download = build?.downloads['server:default'];
        if (!download)
          throw new SoftwareUnavailableError(
            spec.build
              ? `Paper ${spec.mcVersion} の指定ビルドは利用できません`
              : `Paper ${spec.mcVersion} は安定版のビルドがまだ無いため選べません`,
          );
        return {
          url: download.url,
          fileName: download.name,
          hash: { algo: 'sha256', value: download.checksums.sha256 },
        };
      },
    };
  }
  if (family === 'fabric') {
    const loaders = async (mc: string) =>
      fabricLoadersSchema.parse(await fetchJson(`${fabricBase}/loader/${encodeURIComponent(mc)}`));
    return {
      family,
      layoutFamily: 'vanilla',
      listVersions: async () =>
        z
          .array(z.object({ version: z.string(), stable: z.boolean() }))
          .parse(await fetchJson(`${fabricBase}/game`))
          .map((v) => v.version),
      listBuilds: async (mc) => (await loaders(mc)).map((l) => l.loader.version),
      recommendedBuild: async (mc) => (await loaders(mc)).find((l) => l.loader.stable)?.loader.version,
      resolve: async (spec) => {
        check(spec);
        const all = await loaders(spec.mcVersion);
        const loader = spec.loaderVersion
          ? all.find((l) => l.loader.version === spec.loaderVersion)?.loader.version
          : all.find((l) => l.loader.stable)?.loader.version;
        const installer = z
          .array(z.object({ version: z.string(), stable: z.boolean() }))
          .parse(await fetchJson(`${fabricBase}/installer`))
          .find((i) => i.stable)?.version;
        if (!loader || !installer)
          throw new SoftwareUnavailableError(
            `Fabric ${spec.mcVersion} は対応するローダーまたは安定版インストーラが無いため選べません`,
          );
        return {
          url: `${fabricBase}/loader/${encodeURIComponent(spec.mcVersion)}/${encodeURIComponent(loader)}/${encodeURIComponent(installer)}/server/jar`,
          fileName: `fabric-${spec.mcVersion}-${loader}.jar`,
        };
      },
    };
  }
  throw new Error(`未対応のソフト: ${family}`);
}
